import type { PageHelpEntry } from './types.js';

/** Read-only, route-specific training copy; examples never create real accounting entries. */
export const FINANCE_HELP_ENTRIES: readonly PageHelpEntry[] = [
  {
    paths: ['/finance'],
    guide: {
      id: 'finance-overview',
      title: 'نمای کلی مالی',
      purpose:
        'اینجا نقطهٔ شروع کارهای مالی شرکت است. از این صفحه به اسناد حسابداری، دریافتنی‌ها، پرداختنی‌ها، خزانه و گزارش‌ها می‌روی.',
      example:
        'مثال آموزشی: می‌خواهی بدانی مشتریان چه مبلغی بدهکارند؛ از نمای کلی به دریافتنی‌ها برو و ماندهٔ هر مشتری را بررسی کن.',
      steps: [
        'اول دفتر حسابداری انتخاب‌شده را بررسی کن.',
        'بخش مربوط به کارت را باز کن؛ برای پول بانک و صندوق، خزانه و برای سند مالی، اسناد حسابداری را انتخاب کن.',
        'برای بررسی عددها در یک بازهٔ مشخص، وارد گزارش مرتبط شو.',
      ],
      result:
        'می‌دانی اطلاعات مالی موردنظرت در کدام بخش قرار دارد و می‌توانی جزئیات آن را دنبال کنی.',
      tips: [
        'عددهای دفتر قانونی و دفتر مدیریتی را با هم مخلوط نکن.',
        'نمای کلی جای ثبت دوبارهٔ عملیاتی که از فروش، خرید یا خزانه آمده نیست.',
      ],
    },
  },
  {
    paths: ['/finance/journals'],
    guide: {
      id: 'finance-journals',
      title: 'دیدن و ایجاد سند حسابداری',
      purpose:
        'سند حسابداری نشان می‌دهد یک اتفاق مالی روی چه حساب‌هایی اثر گذاشته است. اینجا سندها را پیدا می‌کنی، جزئیاتشان را می‌بینی و پیش‌نویس سند جدید می‌سازی.',
      example:
        'مثال آموزشی: هزینهٔ اینترنت از بانک پرداخت شده است؛ در سند مربوط، هزینهٔ اینترنت بدهکار و بانک بستانکار می‌شود. اگر خزانه قبلاً سند همین پرداخت را ساخته، سند دستی دیگری نساز.',
      steps: [
        'برای پیدا کردن سند، شماره یا شرح آن را جست‌وجو کن و وضعیتش را ببین.',
        'برای ایجاد سند دستی، «سند جدید» را باز کن و دفتر، تاریخ، دورهٔ مالی و شرح را مشخص کن.',
        'حساب و مبلغ بدهکار یا بستانکار هر سطر را وارد کن؛ تفصیلی‌های الزامی را هم تکمیل کن.',
        'وقتی جمع بدهکار و بستانکار برابر شد، «ثبت پیش‌نویس» را بزن و ادامهٔ بررسی را در جزئیات سند انجام بده.',
      ],
      result:
        'سند جدید به‌صورت پیش‌نویس ایجاد می‌شود؛ ذخیرهٔ پیش‌نویس به معنی ثبت قطعی در دفتر نیست.',
      tips: [
        'تراز بودن سند یعنی جمع دو طرف برابر است؛ درست بودن انتخاب حساب‌ها را هم باید بررسی کنی.',
        'قبل از ساخت سند دستی، مطمئن شو سند همان عملیات از فروش، خرید یا خزانه ایجاد نشده است.',
      ],
      comparison: 'journal',
    },
  },
  {
    paths: ['/finance/journals/:journalId'],
    guide: {
      id: 'finance-journal-detail',
      title: 'بررسی و قطعی کردن سند',
      purpose:
        'اینجا سطرها، جمع مبلغ و وضعیت یک سند را می‌بینی. اقدام‌های قابل انجام به وضعیت سند و دسترسی تو بستگی دارند.',
      example:
        'مثال آموزشی: حسابدار یک سند هزینه ساخته است؛ مسئول بررسی سطرها را کنترل می‌کند و سند پس از طی مراحل مجاز، ثبت قطعی می‌شود.',
      steps: [
        'شماره، تاریخ، دفتر و شرح سند را کنترل کن.',
        'حساب‌ها، تفصیلی‌ها و مبلغ هر سطر را بخوان و جمع بدهکار و بستانکار را مقایسه کن.',
        'با توجه به وضعیت سند و دکمه‌هایی که در دسترس داری، مرحلهٔ ارسال برای بررسی، تأیید یا ثبت قطعی را انجام بده.',
        'اگر سند قطعی اشتباه است، از مسیر سند برگشت و توضیح علت استفاده کن.',
      ],
      result: 'وضعیت سند مشخص می‌شود و می‌توانی اثر ثبت قطعی یا سند برگشت مرتبط را دنبال کنی.',
      tips: [
        'سند قطعی ویرایش یا حذف نمی‌شود؛ اصلاح آن با سند برگشت انجام می‌شود.',
        'ندیدن یک دکمه ممکن است به دسترسی تو یا وضعیت فعلی سند مربوط باشد.',
      ],
      comparison: 'journal',
    },
  },
  {
    paths: ['/finance/accounts'],
    guide: {
      id: 'finance-accounts',
      title: 'کدینگ حساب‌ها',
      purpose:
        'کدینگ، فهرست مرتب حساب‌های شرکت است؛ مثل بانک، فروش و هزینهٔ اینترنت. حساب‌ها به شکل شاخه و زیرشاخه نمایش داده می‌شوند.',
      example:
        'مثال آموزشی: برای ثبت هزینهٔ اینترنت، اول بررسی کن حساب مناسب از قبل زیر هزینه‌ها وجود دارد یا باید حساب جدیدی با نظر مسئول مالی تعریف شود.',
      steps: [
        'نام یا کد حساب را جست‌وجو کن و سطح نمایش درخت را انتخاب کن.',
        'برای دیدن اطلاعات بیشتر، حساب موردنظر را باز کن.',
        'اگر حساب جدید لازم است، در «حساب جدید» کد، نام، سطح و حساب والد را مشخص کن.',
        'قبل از ذخیره، ماهیت حساب و امکان ثبت سند روی آن را بررسی کن.',
      ],
      result: 'حساب‌ها در ساختار درست قرار می‌گیرند تا هنگام ثبت سند، حساب مناسب انتخاب شود.',
      tips: [
        'قبل از ایجاد حساب، نام و کاربرد آن را جست‌وجو کن تا حساب تکراری نسازی.',
        'حسابی که فقط دسته‌بندی است، لزوماً نباید قابل ثبت سند باشد.',
      ],
      comparison: 'accounts',
    },
  },
  {
    paths: ['/finance/accounts/:accountId'],
    guide: {
      id: 'finance-account-detail',
      title: 'جزئیات یک حساب',
      purpose:
        'اینجا جای حساب در کدینگ، حساب والد، زیرحساب‌ها و اطلاعات ماندهٔ قابل نمایش را می‌بینی. تنظیمات تفصیلی‌های مجاز این حساب هم در همین صفحه است.',
      example:
        'مثال آموزشی: می‌خواهی هر هزینهٔ اجاره به یک مرکز هزینه وصل باشد؛ تنظیمات تفصیلی حساب هزینه را بررسی می‌کنی و در صورت داشتن دسترسی، مرکز هزینه را الزامی می‌کنی.',
      steps: [
        'نام، کد، سطح و حساب والد را کنترل کن.',
        'وضعیت «قابل ثبت سند» و ماهیت حساب را بررسی کن.',
        'در بخش سطوح تفصیلی شناور، مجاز یا الزامی بودن هر سطح را تنظیم کن؛ اگر دسترسی تغییر داری.',
        'برای بررسی ساختار، زیرحساب‌ها را باز کن.',
      ],
      result: 'مشخص می‌شود روی این حساب چگونه می‌توان سند ثبت کرد و چه اطلاعات تکمیلی لازم است.',
      tips: [
        'تغییر قواعد یک حساب می‌تواند روی ثبت‌های بعدی اثر بگذارد؛ با مسئول مالی هماهنگ کن.',
        'نمایش ندادن مانده را به معنی صفر بودن آن نگیر؛ ممکن است گزارش مانده در دسترس نباشد.',
      ],
      comparison: 'accounts',
    },
  },
  {
    paths: ['/finance/ar'],
    guide: {
      id: 'finance-receivables',
      title: 'دریافتنی‌ها؛ پولی که باید بگیریم',
      purpose:
        'اینجا طلب‌های شرکت از طرف‌حساب‌ها را می‌بینی. «قلم باز» یعنی مبلغی که هنوز کامل تسویه نشده است.',
      example:
        'مثال آموزشی: مشتری بخشی از مبلغ فاکتور را پرداخت کرده است؛ ماندهٔ همان قلم نشان می‌دهد هنوز چه مبلغی باید از او دریافت شود.',
      steps: [
        'تاریخ مبنای بررسی و فیلترهای فهرست را انتخاب کن.',
        'ماندهٔ طرف‌حساب و بخش سررسیدها را بررسی کن تا طلب‌های عقب‌افتاده را پیدا کنی.',
        'قلم موردنظر را باز کن و مبلغ اصلی، مانده و سند منبع را ببین.',
        'برای ثبت دریافت واقعی و ارتباط آن با طلب، از بخش خزانه استفاده کن.',
      ],
      result: 'می‌دانی از چه کسی، بابت چه چیزی و تا چه تاریخی باید پول دریافت شود.',
      tips: [
        'اگر طلب از فاکتور فروش ایجاد شده، همان طلب را دوباره به‌صورت دستی ثبت نکن.',
        'دیدن یک طلب یا انتخاب آن در فهرست به معنی دریافت پول نیست.',
      ],
      comparison: 'parties',
    },
  },
  {
    paths: ['/finance/ar/:openItemId'],
    guide: {
      id: 'finance-receivable-detail',
      title: 'جزئیات یک طلب',
      purpose:
        'اینجا مبلغ اصلی یک طلب، بخش تسویه‌شده، مانده، طرف‌حساب و سررسید آن را بررسی می‌کنی.',
      example:
        'مثال آموزشی: مشتری می‌پرسد چرا هنوز بدهکار است؛ این صفحه مبلغ اولیه و باقی‌ماندهٔ همان طلب را کنار سند منبع نشان می‌دهد.',
      steps: [
        'نام طرف‌حساب و شمارهٔ منبع را با مدرکی که داری مقایسه کن.',
        'مبلغ اصلی، تسویه‌شده و مانده را بخوان.',
        'سررسید و وضعیت قلم را بررسی کن.',
        'در صورت نیاز، لینک سند حسابداری یا سند منبع را باز کن؛ برای تخصیص دریافت‌ها به جزئیات گردش خزانه برو.',
      ],
      result: 'وضعیت همین طلب و ارتباطش با سند اولیه روشن می‌شود.',
      tips: [
        'این صفحه فهرست جداگانهٔ همهٔ تخصیص‌ها را نشان نمی‌دهد؛ جزئیات تخصیص در گردش خزانه است.',
        'ماندهٔ صفر مربوط به همین قلم است و لزوماً ماندهٔ کل طرف‌حساب نیست.',
      ],
      comparison: 'parties',
    },
  },
  {
    paths: ['/finance/ap'],
    guide: {
      id: 'finance-payables',
      title: 'پرداختنی‌ها؛ پولی که باید بدهیم',
      purpose:
        'اینجا بدهی‌های شرکت به طرف‌حساب‌ها و تأمین‌کنندگان را می‌بینی. «قلم پرداختنی باز» یعنی بدهی‌ای که هنوز کامل تسویه نشده است.',
      example:
        'مثال آموزشی: یک فاکتور خرید نسیه ثبت شده و سررسیدش نزدیک است؛ در این صفحه مانده و تاریخ پرداخت آن را پیدا می‌کنی.',
      steps: [
        'تاریخ مبنای بررسی و فیلترهای فهرست را مشخص کن.',
        'ماندهٔ تأمین‌کنندگان و زمان‌بندی بدهی‌ها را بخوان.',
        'قلم موردنظر را باز کن و منبع بدهی را بررسی کن.',
        'برای ثبت پرداخت واقعی و تخصیص آن به بدهی، به خزانه برو.',
      ],
      result: 'می‌توانی بدهی‌های باز و سررسیدهایشان را برای پیگیری پرداخت مشخص کنی.',
      tips: [
        'اگر بدهی از خرید آمده، ثبت دستی دوبارهٔ آن بدهی را تکراری می‌کند.',
        'زمان‌بندی این صفحه نشان‌دهندهٔ سررسیدهاست؛ خودبه‌خود پولی از بانک پرداخت نمی‌کند.',
      ],
      comparison: 'parties',
    },
  },
  {
    paths: ['/finance/ap/:openItemId'],
    guide: {
      id: 'finance-payable-detail',
      title: 'جزئیات یک بدهی',
      purpose:
        'اینجا مبلغ اولیه، مبلغ تسویه‌شده و ماندهٔ یک بدهی را همراه با سررسید و سند منبع آن می‌بینی.',
      example:
        'مثال آموزشی: تأمین‌کننده دربارهٔ ماندهٔ یک فاکتور سؤال دارد؛ شمارهٔ منبع و مبلغ باقی‌مانده را در همین صفحه کنترل می‌کنی.',
      steps: [
        'طرف‌حساب و شمارهٔ منبع بدهی را بررسی کن.',
        'مبلغ اصلی، مبلغ تسویه‌شده و مانده را مقایسه کن.',
        'سررسید و وضعیت بدهی را بخوان.',
        'برای بررسی ثبت اولیه، سند حسابداری یا منبع آن را باز کن؛ جزئیات تخصیص پرداخت در گردش خزانه است.',
      ],
      result: 'مشخص می‌شود از همین بدهی چه مبلغی باقی مانده و ثبت اولیه از کجا آمده است.',
      tips: [
        'ثبت پرداخت باید به بدهی درست متصل شود؛ صرف وجود یک پرداخت، تسویهٔ این قلم را ثابت نمی‌کند.',
        'ماندهٔ این قلم را با ماندهٔ کل تأمین‌کننده یکی نگیر.',
      ],
      comparison: 'parties',
    },
  },
  {
    paths: ['/finance/treasury'],
    guide: {
      id: 'finance-treasury',
      title: 'خزانه؛ دریافت و پرداخت پول',
      purpose:
        'اینجا حساب‌های بانک و صندوق و گردش‌های دریافت و پرداخت را مدیریت می‌کنی. می‌توانی دریافت یا پرداخت را به طلب یا بدهی مرتبط وصل کنی.',
      example:
        'مثال آموزشی: مشتری بابت دو فاکتور پول واریز کرده است؛ دریافت را روی حساب خزانهٔ درست ثبت می‌کنی و مبلغ آن را بین همان دو طلب تقسیم می‌کنی.',
      steps: [
        'حساب خزانه و نوع عملیات دریافت یا پرداخت را درست انتخاب کن.',
        'طرف‌حساب، مبلغ، تاریخ و اطلاعات لازم فرم را وارد کن.',
        'اگر عملیات برای تسویه است، اقلام باز همان طرف‌حساب را انتخاب و مبلغ تخصیص هرکدام را وارد کن.',
        'جمع تخصیص‌ها را با مبلغ عملیات مقایسه کن و پس از ثبت، وضعیت گردش و سند مربوط را بررسی کن.',
      ],
      result:
        'یک گردش خزانه با سند مرتبط ایجاد می‌شود و وضعیت تسویهٔ اقلام طبق نتیجهٔ ثبت قابل پیگیری است.',
      tips: [
        'وقتی تخصیص داری، جمع آن باید دقیقاً برابر مبلغ عملیات و همهٔ اقلام متعلق به یک طرف‌حساب باشند.',
        'ثبت در نرم‌افزار، انتقال واقعی پول در بانک نیست؛ وضعیت پیش‌نویس و قطعی را هم بررسی کن.',
      ],
      comparison: 'treasury',
    },
  },
  {
    paths: ['/finance/treasury/transactions/:treasuryTransactionId'],
    guide: {
      id: 'finance-treasury-detail',
      title: 'جزئیات یک دریافت یا پرداخت',
      purpose:
        'اینجا مشخصات یک گردش خزانه، وضعیت آن، سند حسابداری و تخصیص‌های مرتبط با طلب‌ها یا بدهی‌ها را می‌بینی.',
      example:
        'مثال آموزشی: برای پیدا کردن اینکه یک واریز مشتری کدام فاکتورها را پوشش داده، جدول «تخصیص به اقلام باز» را در گردش مربوط بررسی می‌کنی.',
      steps: [
        'نوع گردش، مبلغ، تاریخ و وضعیت آن را کنترل کن.',
        'اگر اطلاعات حساب خزانه نمایش داده شده، حساب مقصد یا مبدأ را بررسی کن.',
        'جدول تخصیص را بخوان و قلم باز موردنظر را باز کن.',
        'برای بررسی اثر حسابداری، «مشاهدهٔ سند» را باز کن.',
      ],
      result: 'ارتباط همین دریافت یا پرداخت با سند حسابداری و اقلام تسویه‌شده مشخص می‌شود.',
      tips: [
        'نبودن جدول تخصیص می‌تواند یعنی این گردش تخصیص تسویه ندارد.',
        'گردش پیش‌نویس را معادل سند حسابداری قطعی حساب نکن.',
      ],
      comparison: 'treasury',
    },
  },
  {
    paths: ['/finance/cheques', '/finance/treasury/cheques'],
    guide: {
      id: 'finance-cheques',
      title: 'پیگیری چک‌ها',
      purpose:
        'اینجا چک‌های دریافتی و پرداختی، سررسیدها و وضعیتشان را پیگیری می‌کنی. اطلاعات دسته‌چک‌ها هم در همین بخش قرار دارد.',
      example:
        'مثال آموزشی: مشتری یک چک داده است؛ مشخصاتش را به‌عنوان چک دریافتی ثبت می‌کنی و بعد، تغییر وضعیت واقعی آن را در نرم‌افزار دنبال می‌کنی.',
      steps: [
        'بخش چک دریافتی، پرداختی یا دسته‌چک را متناسب با کارت انتخاب کن.',
        'برای ثبت چک، شناسهٔ صیاد، سریال، مبلغ، سررسید و حساب‌های لازم را با اصل چک تطبیق بده.',
        'برای پیدا کردن چک، جست‌وجو و فیلتر وضعیت را به کار ببر.',
        'تغییر وضعیت را فقط مطابق اتفاق واقعی و اقدام‌های مجاز همان چک ثبت کن.',
      ],
      result: 'می‌دانی هر چک چه مبلغ و سررسیدی دارد و اکنون در چه وضعیتی است.',
      tips: [
        'ثبت چک در ERP جای ثبت، تأیید یا استعلام آن در سامانه‌های بانکی را نمی‌گیرد.',
        'اگر مشخصات از تصویر استخراج شد، قبل از تأیید همهٔ فیلدها را با اصل چک کنترل کن.',
      ],
      comparison: 'cheques',
    },
  },
  {
    paths: ['/finance/bank-recon'],
    guide: {
      id: 'finance-bank-reconciliation',
      title: 'مغایرت‌گیری بانک',
      purpose:
        'اینجا گردش حساب بانک را با ثبت‌های خزانه مقایسه می‌کنی تا مواردی مثل ثبت جاافتاده، ثبت تکراری یا تفاوت زمانی مشخص شود.',
      example:
        'مثال آموزشی: بانک یک کارمزد کم کرده ولی هنوز در ثبت‌های شرکت دیده نمی‌شود؛ بررسی صورتحساب کمک می‌کند این تفاوت را پیدا کنی.',
      steps: [
        'حساب بانکی درست را انتخاب کن.',
        'صورتحساب مربوط را با بازهٔ تاریخ و مانده‌های درست وارد کن.',
        'تطبیق خودکار را بررسی کن و موارد باقی‌مانده را با گردش متناظر خزانه تطبیق بده.',
        'برای تاریخ موردنظر، صورت مغایرت را ثبت و اختلاف مانده‌ها را بررسی کن.',
      ],
      result: 'تراکنش‌های تطبیق‌یافته و موارد باز مشخص می‌شوند و سابقهٔ مغایرت‌گیری باقی می‌ماند.',
      tips: [
        'قبل از تطبیق دستی، مبلغ، تاریخ و شمارهٔ پیگیری دو طرف را کنترل کن.',
        'پیدا شدن مغایرت به‌خودی‌خود مجوز ساخت سند اصلاحی یا حذف ثبت قبلی نیست.',
      ],
    },
  },
  {
    paths: ['/finance/sales-accounts'],
    guide: {
      id: 'finance-sales-posting-accounts',
      title: 'حساب‌های فروش و پورسانت',
      purpose:
        'اینجا مسئول مالی مشخص می‌کند سند فاکتور فروش و پورسانت روی کدام حساب‌های حسابداری ثبت شود.',
      example:
        'مثال آموزشی: برای یکپارچه بودن اسناد فروش، حساب دریافتنی، درآمد فروش و مالیات را در مقصد فاکتور تنظیم می‌کنی؛ فروشنده هنگام صدور فاکتور این حساب‌ها را از نو انتخاب نمی‌کند.',
      steps: [
        'در بخش مقصد فاکتور فروش، حساب دریافتنی، درآمد و مالیات را بررسی کن.',
        'در بخش پورسانت، حساب هزینه و حساب پورسانت پرداختنی را بررسی کن.',
        'اگر دسترسی مدیریت داری، تنظیمات هر بخش را جداگانه ذخیره کن.',
        'پیام نتیجه را بخوان و پیش از استفادهٔ عملیاتی، انتخاب حساب‌ها را با مسئول مالی کنترل کن.',
      ],
      result: 'مقصد حسابداری عملیات فروش و پورسانت طبق تنظیمات ذخیره‌شده مشخص می‌شود.',
      tips: [
        'حساب مناسب را بر اساس کدینگ شرکت انتخاب کن؛ مشابه بودن نام دو حساب کافی نیست.',
        'این تنظیمات را برای تغییر دستی مبلغ فاکتور یا پورسانت استفاده نکن.',
      ],
    },
  },
  {
    paths: ['/finance/vat'],
    guide: {
      id: 'finance-vat',
      title: 'بررسی ارزش افزوده',
      purpose:
        'اینجا ثبت‌های مالیات بر ارزش افزوده و خلاصهٔ دوره را می‌بینی و کارهای مربوط به اظهارنامهٔ دوره‌ای را پیگیری می‌کنی. این بخش برای دفتر قانونی است.',
      example:
        'مثال آموزشی: پیش از تهیهٔ اظهارنامهٔ یک فصل، ثبت‌های خرید و فروش همان بازه را بررسی می‌کنی تا مأخذ و مبلغ مالیات کنترل شوند.',
      steps: [
        'دفتر قانونی و بازهٔ تاریخ موردنظر را انتخاب کن.',
        'ثبت‌های ارزش افزوده و خلاصهٔ دوره را با اسناد خرید و فروش مقایسه کن.',
        'برای تهیهٔ اظهارنامه، سال مالی، فصل، شروع و پایان دوره و مهلت تسلیم را مشخص کن.',
        'رهگیری تسلیم یا پرداخت را فقط با اتکا به مدرک واقعی آن ثبت و وضعیت را بررسی کن.',
      ],
      result: 'ثبت‌ها و وضعیت اظهارنامه‌های دوره قابل پیگیری می‌شوند.',
      tips: [
        'نمایش یا تهیهٔ اظهارنامه در این صفحه، به‌تنهایی تأیید دریافت آن توسط سازمان مالیاتی نیست.',
        'نرخ، مشمولیت و تکالیف مالیاتی را با مسئول مالی و مقررات معتبر همان دوره کنترل کن.',
      ],
    },
  },
  {
    paths: ['/finance/modian'],
    guide: {
      id: 'finance-moadian',
      title: 'پیگیری صورتحساب‌های مودیان',
      purpose:
        'اینجا تنظیمات اتصال و وضعیت ارسال و استعلام صورتحساب الکترونیکی را می‌بینی. این بخش با دفتر قانونی کار می‌کند.',
      example:
        'مثال آموزشی: یک صورتحساب ارسال شده و هنوز پاسخ نهایی ندارد؛ وضعیتش را استعلام می‌کنی تا نتیجهٔ پردازش مشخص شود.',
      steps: [
        'تنظیمات اتصال، شناسهٔ حافظهٔ مالیاتی و محیط آزمایشی یا عملیاتی را با مسئول مربوط کنترل کن.',
        'صورتحساب را پیدا کن و قبل از ارسال، جزئیات خریدار و اقلام را بررسی کن.',
        'پس از ارسال، وضعیت و شمارهٔ رهگیری را دنبال کن.',
        'در انتظار پردازش یا خطا، نتیجهٔ استعلام و پیام سامانه را بررسی کن.',
      ],
      result: 'می‌توانی وضعیت ارسال، پاسخ سامانه و اطلاعات رهگیری هر صورتحساب را دنبال کنی.',
      tips: [
        '«در انتظار پردازش» به معنی تأیید نهایی نیست؛ نتیجهٔ سامانه را بررسی کن.',
        'صورتحساب ارسال‌شده را مثل یک پیش‌نویس قابل ویرایش در نظر نگیر؛ اصلاح آن مسیر مخصوص خودش را دارد.',
      ],
    },
  },
  {
    paths: ['/finance/books'],
    guide: {
      id: 'finance-books',
      title: 'دفترهای حسابداری',
      purpose:
        'دفتر قانونی و دفتر مدیریتی، دو مجموعهٔ جدا از ثبت‌های حسابداری هستند. اینجا دفترهای موجود و وضعیت فعال یا غیرفعالشان را می‌بینی.',
      example:
        'مثال آموزشی: برای یک گزارش داخلی، دفتر مدیریتی را انتخاب می‌کنی؛ برای ارزش افزوده و مودیان باید دفتر قانونی مبنا باشد.',
      steps: [
        'نام، کد، نوع و وضعیت دفترها را بررسی کن.',
        'پیش از ثبت سند یا خواندن گزارش، دفتر انتخاب‌شده را کنترل کن.',
        'اگر دسترسی مدیریت دفتر داری و دفتر دیگری لازم است، اطلاعات «دفتر جدید» را تکمیل کن.',
        'تغییر وضعیت دفتر غیرقانونی را فقط پس از هماهنگی با مسئول مالی انجام بده.',
      ],
      result: 'ثبت‌ها و گزارش‌های هر دفتر را در زمینهٔ درست دنبال می‌کنی.',
      tips: [
        'دفتر قانونی حذف یا غیرفعال نمی‌شود و مبنای ارزش افزوده و مودیان است.',
        'مبلغ‌های دو دفتر را بدون بررسی نوع و هدفشان با هم جمع یا مقایسه نکن.',
      ],
    },
  },
  {
    paths: ['/finance/dimensions'],
    guide: {
      id: 'finance-dimensions',
      title: 'سطوح تفصیلی؛ ثبت جزئیات هزینه و درآمد',
      purpose:
        'سطح تفصیلی به تو می‌گوید یک مبلغ مربوط به کدام پروژه، مرکز هزینه، شعبه یا قرارداد است؛ بدون اینکه برای هر مورد یک حساب تازه بسازی.',
      example:
        'مثال آموزشی: «مرکز هزینه» یک سطح است و «واحد فروش» و «واحد پشتیبانی» دو مورد داخل آن هستند.',
      steps: [
        'اول سطح مناسب، مثل مرکز هزینه یا پروژه، را انتخاب یا تعریف کن.',
        'برای آن سطح، موارد موردنیاز را با کد و نام مشخص اضافه کن.',
        'از جزئیات حساب، بررسی کن کدام سطح برای ثبت روی آن حساب مجاز یا الزامی است.',
        'هنگام ثبت سند، مورد درست را در سطر مربوط انتخاب کن.',
      ],
      result: 'گزارش‌های مالی می‌توانند درآمد و هزینه را با جزئیات قابل پیگیری نشان بدهند.',
      tips: [
        'نام سطح را با نام یک مورد داخل آن اشتباه نگیر؛ «پروژه» با نام یک پروژه متفاوت است.',
        'قبل از ساخت مورد جدید، موارد موجود را بررسی کن تا یک پروژه با چند نام ثبت نشود.',
      ],
    },
  },
  {
    paths: ['/finance/fiscal-periods'],
    guide: {
      id: 'finance-fiscal-periods',
      title: 'سال و دورهٔ مالی',
      purpose:
        'سال مالی بازهٔ اصلی گزارش‌گیری شرکت است و دوره‌های مالی بخش‌های کوچک‌تر آن هستند. اینجا سال جدید و وضعیت باز یا بستهٔ دوره‌ها را مدیریت می‌کنی.',
      example:
        'مثال آموزشی: پس از کنترل ثبت‌های یک ماه، مسئول مالی دورهٔ مربوط را می‌بندد تا ثبت‌های آن دوره بدون فرایند مجاز تغییر نکنند.',
      steps: [
        'سال‌ها و دوره‌های موجود را بررسی کن.',
        'برای سال جدید، کد، عنوان و تاریخ شروع و پایان را مشخص کن؛ دوره‌های ماهانه خودکار ساخته می‌شوند.',
        'برای بستن یک دوره، نام دوره را کنترل کن و پیام تأیید را بخوان.',
        'اگر بازگشایی لازم است، با دسترسی مجاز و توضیح علت از همان مسیر استفاده کن.',
      ],
      result: 'بازه‌های مالی و امکان ثبت در دوره‌ها مشخص می‌شود.',
      tips: [
        'بستن یک دوره با عملیات بستن سال مالی فرق دارد.',
        'قبل از بستن دوره، ثبت‌های معوق و سندهای در انتظار بررسی را با مسئول مالی کنترل کن.',
      ],
    },
  },
  {
    paths: ['/finance/year-end'],
    guide: {
      id: 'finance-year-end',
      title: 'بستن سال مالی',
      purpose:
        'این صفحه برای بررسی و آماده‌سازی اسناد پایان سال و انتقال مانده‌های لازم به سال بعد است. ابتدا نتیجه را می‌بینی و بعد عملیات مجاز را انجام می‌دهی.',
      example:
        'مثال آموزشی: پایان سال رسیده است؛ مسئول مالی ابتدا موانع و پیش‌نمایش اسناد را کنترل می‌کند و بعد اسناد بستن را تهیه می‌کند.',
      steps: [
        'سال مالی، سال بعد و حساب‌های لازم را با دقت انتخاب کن.',
        'پیش‌نمایش را بخوان و موانع بستن را برطرف کن.',
        'سطرها و مانده‌های اسناد پیشنهادی را بررسی کن و سپس تهیهٔ اسناد را انجام بده.',
        'وضعیت اسناد ساخته‌شده و مراحل بعدی را از همین صفحه و جزئیات سند پیگیری کن.',
      ],
      result: 'اسناد مرتبط با پایان سال و وضعیت عملیات قابل بررسی و پیگیری می‌شوند.',
      tips: [
        'تهیهٔ پیش‌نویس با ثبت قطعی یکسان نیست؛ وضعیت هر سند را ببین.',
        'برگرداندن عملیات فقط در شرایط مجاز ممکن است؛ سند قطعی با سند برگشت اصلاح می‌شود.',
      ],
      comparison: 'opening',
    },
  },
  {
    paths: ['/finance/fixed-assets'],
    guide: {
      id: 'finance-fixed-assets',
      title: 'دارایی‌های ثابت و استهلاک',
      purpose:
        'اینجا اموالی مثل رایانه، خودرو یا تجهیزات شرکت را ثبت و هزینهٔ استهلاک آن‌ها را پیگیری می‌کنی. استهلاک یعنی سرشکن شدن بهای دارایی در طول مدت استفاده از آن.',
      example:
        'مثال آموزشی: شرکت یک رایانه خریده است؛ آن را با تاریخ تحصیل، بهای تمام‌شده و عمر مفید ثبت می‌کنی تا استهلاک دوره‌ای آن قابل محاسبه باشد.',
      steps: [
        'فهرست دارایی‌ها را بررسی کن تا دارایی تکراری ثبت نشود.',
        'برای دارایی جدید، کد، عنوان، دسته، تاریخ تحصیل و مبلغ‌ها را تکمیل کن.',
        'برای استهلاک، سال، دورهٔ مالی و تاریخ ثبت سند را انتخاب کن.',
        'نتیجهٔ اجرا و سند مرتبط را با اطلاعات دارایی کنترل کن.',
      ],
      result: 'مشخصات دارایی و سوابق استهلاک آن در شرکت قابل پیگیری می‌شود.',
      tips: [
        'بهای تمام‌شده، ارزش اسقاط و عمر مفید را با مدرک و تأیید مسئول مالی وارد کن.',
        'کالاهای قابل فروش در انبار را بدون بررسی، دارایی ثابت ثبت نکن.',
      ],
    },
  },
  {
    paths: ['/finance/currencies'],
    guide: {
      id: 'finance-currencies',
      title: 'ارز و نرخ برابری',
      purpose:
        'اینجا واحدهای پول، نرخ‌های ثبت‌شده و عملیات تسعیر را پیگیری می‌کنی. تسعیر یعنی محاسبهٔ اثر تغییر نرخ ارز روی مانده‌های ارزی.',
      example:
        'مثال آموزشی: شرکت یک طلب ارزی دارد و نرخ پایان دوره تغییر کرده است؛ مسئول مالی نرخ معتبر را ثبت و نتیجهٔ تسعیر را بررسی می‌کند.',
      steps: [
        'ارزهای موجود و ارز پایهٔ سازمان را بررسی کن.',
        'برای نرخ جدید، ارز، تاریخ، نوع نرخ، مقدار ریالی هر واحد و منبع استعلام را وارد کن.',
        'برای تسعیر، سال، دوره، ارز و تاریخ اعمال را انتخاب کن.',
        'سوابق اجرا و سند پیش‌نویس ساخته‌شده را بررسی کن.',
      ],
      result: 'نرخ‌های معتبر و اثر محاسبه‌شدهٔ تسعیر با سابقهٔ مشخص نگهداری می‌شوند.',
      tips: [
        'نرخ را به واحدی که فرم خواسته وارد کن؛ ریال و تومان را جابه‌جا نکن.',
        'نرخ‌های ثبت‌شده سوابق تغییرناپذیرند و سند تسعیر ابتدا پیش‌نویس است.',
      ],
    },
  },
  {
    paths: ['/finance/cost-allocations'],
    guide: {
      id: 'finance-cost-allocations',
      title: 'تسهیم هزینه؛ تقسیم هزینهٔ مشترک',
      purpose:
        'تسهیم یعنی تقسیم یک هزینهٔ مشترک بین مقصدهای مشخص بر اساس یک قاعده. اینجا قاعده را تعریف می‌کنی، نتیجه را شبیه‌سازی می‌کنی و سپس اجرا را پیگیری می‌کنی.',
      example:
        'مثال آموزشی: یک هزینهٔ مشترک باید با نسبت‌های تأییدشده بین چند حساب مقصد تقسیم شود؛ قبل از ساخت سند، نتیجهٔ محاسبه را می‌بینی.',
      steps: [
        'قاعدهٔ موردنظر، حساب مبدأ، حساب واسط و مقصدها را بررسی یا تعریف کن.',
        'سال، دوره و تاریخ مبنای مانده را برای شبیه‌سازی مشخص کن.',
        'سهم مقصدها و جمع مبلغ را در نتیجهٔ شبیه‌سازی کنترل کن.',
        'پس از تأیید مسئول مالی، اجرا را انجام بده و وضعیت سند پیش‌نویس را در سوابق ببین.',
      ],
      result: 'تقسیم هزینه با قاعدهٔ مشخص و سند قابل پیگیری انجام می‌شود.',
      tips: [
        'شبیه‌سازی فقط نتیجه را نشان می‌دهد و خودش سندی ثبت نمی‌کند.',
        'قاعده یا نسبت تقسیم را صرفاً برای صفر شدن اختلاف‌ها تغییر نده؛ مبنای تقسیم باید تأیید شده باشد.',
      ],
    },
  },
  {
    paths: ['/finance/reports'],
    guide: {
      id: 'finance-reports',
      title: 'انتخاب گزارش مالی',
      purpose:
        'این صفحه فهرست گزارش‌های مالی است. بسته به سؤال خودت، گزارش ماندهٔ حساب‌ها، گردش حساب یا صورت مالی مناسب را باز کن.',
      example:
        'مثال آموزشی: برای دیدن سود یک دوره، صورت سود و زیان را انتخاب کن؛ برای بررسی ریز ثبت‌های یک حساب، دفتر کل یا معین را باز کن.',
      steps: [
        'اول مشخص کن دنبال مانده، گردش یا نتیجهٔ مالی دوره هستی.',
        'گزارش مرتبط را از فهرست باز کن.',
        'در صفحهٔ گزارش، دفتر، تاریخ و فیلترهای لازم را مشخص کن.',
        'برای اختلاف‌ها، از جمع گزارش به ریز سندهای مرتبط برو.',
      ],
      result: 'گزارشی را می‌خوانی که پاسخ سؤال مالی تو را در بازهٔ درست می‌دهد.',
      tips: [
        'گزارش‌های مالی قطعی را با فهرست پیش‌نویس‌ها مقایسه نکن.',
        'اگر یک گزارش در محیط تو در دسترس نیست، از عددهای گزارش دیگری به‌عنوان جایگزین قطعی استفاده نکن.',
      ],
      comparison: 'reports',
    },
  },
  {
    paths: ['/finance/reports/trial-balance'],
    guide: {
      id: 'finance-trial-balance',
      title: 'تراز آزمایشی؛ ماندهٔ حساب‌ها',
      purpose:
        'این گزارش ماندهٔ بدهکار و بستانکار حساب‌ها را تا یک تاریخ نشان می‌دهد. مبنای آن سندهای قطعی است.',
      example:
        'مثال آموزشی: می‌خواهی ماندهٔ حساب بانک را در پایان یک ماه کنترل کنی؛ تاریخ پایان ماه را انتخاب و سطر بانک را پیدا می‌کنی.',
      steps: [
        'دفتر حسابداری موردنظر را کنترل کن.',
        '«تا تاریخ» را مشخص کن.',
        'در صورت نیاز، نمایش فقط حساب‌های با گردش را انتخاب کن.',
        'ماندهٔ حساب و جمع دو طرف را بخوان و برای بررسی اختلاف به سندهای مربوط رجوع کن.',
      ],
      result: 'ماندهٔ حساب‌ها در تاریخ انتخاب‌شده را بر اساس ثبت‌های قطعی می‌بینی.',
      tips: [
        'سندی که هنوز پیش‌نویس است، وارد این گزارش نمی‌شود.',
        'برابری جمع بدهکار و بستانکار به‌تنهایی درست بودن همهٔ حساب‌ها را ثابت نمی‌کند.',
      ],
      comparison: 'reports',
    },
  },
  {
    paths: ['/finance/reports/trial-balance-columns'],
    guide: {
      id: 'finance-trial-balance-columns',
      title: 'تراز چندستونی',
      purpose:
        'اینجا گردش و ماندهٔ حساب‌ها را در قالب‌های دو، چهار، شش یا هشت‌ستونی می‌بینی. ستون‌های بیشتر برای جدا دیدن بخش‌های گردش و مانده هستند.',
      example:
        'مثال آموزشی: مسئول مالی می‌خواهد گردش دوره و ماندهٔ پایان آن را کنار هم کنترل کند؛ قالب مناسب و بازهٔ موردنظر را انتخاب می‌کند.',
      steps: [
        'نوع تراز و سطح حساب را انتخاب کن.',
        'دفتر و تاریخ‌های گزارش را کنترل کن.',
        'عنوان ستون‌ها را بخوان تا گردش را با مانده اشتباه نگیری.',
        'حساب‌های دارای ماندهٔ خلاف ماهیت را بررسی کن و در صورت نیاز ریز ثبت‌ها را دنبال کن.',
      ],
      result: 'گردش و ماندهٔ حساب‌ها در قالب انتخاب‌شده و بر مبنای اسناد قطعی نمایش داده می‌شود.',
      tips: [
        'قالب دو‌ستونی و هشت‌ستونی اطلاعات یکسانی را به یک شکل نشان نمی‌دهند؛ عنوان ستون‌ها را کنترل کن.',
        'هشدار خلاف ماهیت، نیاز به بررسی دارد و به‌تنهایی اثبات خطای ثبت نیست.',
      ],
      comparison: 'reports',
    },
  },
  {
    paths: ['/finance/reports/ledger'],
    guide: {
      id: 'finance-ledger',
      title: 'دفتر کل، معین و تفصیلی',
      purpose:
        'اینجا ریز گردش حساب یا تفصیلی را می‌بینی؛ یعنی چه سندهایی باعث تغییر ماندهٔ آن شده‌اند.',
      example:
        'مثال آموزشی: ماندهٔ هزینهٔ یک پروژه غیرمنتظره است؛ تفصیلی پروژه و بازهٔ موردنظر را انتخاب می‌کنی و ثبت‌های مؤثر را می‌خوانی.',
      steps: [
        'نوع دفتر را انتخاب کن؛ کل، معین یا تفصیلی.',
        'حساب یا سطح و مورد تفصیلی لازم را مشخص کن.',
        'از تاریخ، تا تاریخ و دفتر حسابداری را بررسی کن.',
        'ماندهٔ ابتدا، گردش سطرها و ماندهٔ جاری را بخوان؛ برای جزئیات بیشتر سند مرتبط را باز کن.',
      ],
      result: 'می‌توانی مسیر رسیدن به ماندهٔ حساب را از روی سندهای قطعی دنبال کنی.',
      tips: [
        'حساب کل، معین و تفصیلی محدودهٔ یکسانی ندارند؛ انتخابت را قبل از مقایسه کنترل کن.',
        'مبلغ بدهکار یا بستانکار یک سطر را با ماندهٔ جاری پس از آن اشتباه نگیر.',
      ],
      comparison: 'reports',
    },
  },
  {
    paths: ['/finance/reports/income-statement'],
    guide: {
      id: 'finance-income-statement',
      title: 'صورت سود و زیان',
      purpose:
        'این گزارش درآمدها و هزینه‌های ثبت‌شده در یک بازه را کنار هم می‌گذارد تا نتیجهٔ مالی آن دوره مشخص شود. فقط سندهای قطعی مبنا هستند.',
      example:
        'مثال آموزشی: می‌خواهی نتیجهٔ سه ماه گذشته را بررسی کنی؛ بازه را انتخاب و درآمدها، هزینه‌ها و سود یا زیان را می‌خوانی.',
      steps: [
        'دفتر حسابداری را مشخص کن.',
        'سال مالی یا تاریخ شروع و پایان را انتخاب کن.',
        'بخش درآمدها و هزینه‌ها را جداگانه بررسی کن.',
        'نتیجهٔ نهایی را با دوره‌ای با شرایط و طول مشابه مقایسه کن.',
      ],
      result: 'نتیجهٔ درآمد و هزینهٔ ثبت‌شده در دورهٔ انتخاب‌شده را می‌بینی.',
      tips: [
        'سود با موجودی بانک یکی نیست؛ فروش نسیه و پرداخت‌ها می‌توانند زمان متفاوتی داشته باشند.',
        'سندهای پیش‌نویس در این گزارش قطعی دیده نمی‌شوند.',
      ],
      comparison: 'reports',
    },
  },
  {
    paths: ['/finance/reports/balance-sheet'],
    guide: {
      id: 'finance-balance-sheet',
      title: 'ترازنامه',
      purpose:
        'ترازنامه تصویر وضعیت مالی شرکت در یک تاریخ است: چه دارایی‌هایی دارد، چه بدهی‌هایی دارد و سهم صاحبان شرکت چقدر است.',
      example:
        'مثال آموزشی: برای بررسی وضعیت پایان سال، تاریخ آخر سال را انتخاب می‌کنی و دارایی‌ها را کنار بدهی‌ها و حقوق صاحبان سهام می‌بینی.',
      steps: [
        'دفتر حسابداری را کنترل کن.',
        'تاریخ موردنظر را در «تا تاریخ» وارد کن.',
        'بخش‌های دارایی، بدهی و حقوق صاحبان سهام را بخوان.',
        'جمع‌ها و سود یا زیان سال جاری را بررسی کن و اختلاف‌ها را از ریز حساب‌ها دنبال کن.',
      ],
      result: 'وضعیت مالی در همان تاریخ و بر پایهٔ سندهای قطعی مشخص می‌شود.',
      tips: [
        'ترازنامه مربوط به یک تاریخ است؛ آن را با گزارش گردش یک بازه یکی نگیر.',
        'برای مقایسه، تاریخ و دفتر دو گزارش باید هم‌خوان باشند.',
      ],
      comparison: 'reports',
    },
  },
  {
    paths: ['/finance/reports/cash-flow'],
    guide: {
      id: 'finance-cash-flow',
      title: 'صورت جریان وجوه نقد',
      purpose:
        'این گزارش ورود و خروج پول نقد را در فعالیت‌های عملیاتی، سرمایه‌گذاری و تأمین مالی نشان می‌دهد. مبنای آن ثبت‌های قطعی است.',
      example:
        'مثال آموزشی: شرکت سود دارد ولی نقدینگی کم شده است؛ این گزارش کمک می‌کند مسیرهای ورود و خروج وجه نقد دوره را بررسی کنی.',
      steps: [
        'دفتر، سال مالی یا تاریخ شروع و پایان را مشخص کن.',
        'جریان‌های فعالیت عملیاتی، سرمایه‌گذاری و تأمین مالی را جداگانه بخوان.',
        'موجودی نقد و بانک ابتدا و انتهای دوره را مقایسه کن.',
        'برای بررسی عددهای غیرمنتظره، به ثبت‌ها و گردش‌های مرتبط برگرد.',
      ],
      result: 'تغییرات وجه نقد در بازهٔ انتخاب‌شده و دسته‌بندی آن را می‌بینی.',
      tips: [
        'این صورت مالی با نمودار گردش اخیر خزانه یا پیش‌بینی نقدینگی یکسان نیست.',
        'فروش ثبت‌شده الزاماً به معنی وصول پول در همان دوره نیست.',
      ],
      comparison: 'reports',
    },
  },
  {
    paths: ['/finance/reports/profitability'],
    guide: {
      id: 'finance-profitability',
      title: 'سودآوری به تفکیک',
      purpose:
        'اینجا درآمد و هزینه را به تفکیک مرکز هزینه، پروژه، مشتری یا محصول بررسی می‌کنی تا نتیجهٔ هر بخش روشن شود.',
      example:
        'مثال آموزشی: می‌خواهی نتیجهٔ پروژه‌های شرکت را مقایسه کنی؛ بُعد «پروژه‌ها» و بازهٔ موردنظر را انتخاب می‌کنی.',
      steps: [
        'بُعد تحلیلی را متناسب با سؤال خودت انتخاب کن.',
        'دفتر و بازهٔ تاریخ را کنترل کن.',
        'درآمد، هزینه، سود یا زیان و حاشیه سود هر مورد را کنار هم بخوان.',
        'برای نتیجهٔ ناقص یا غیرمنتظره، بررسی کن ثبت‌های مربوط با تفصیلی درست انجام شده باشند.',
      ],
      result: 'نتیجهٔ ثبت‌های قطعی را برای هر مورد از بُعد انتخاب‌شده می‌بینی.',
      tips: [
        'تفکیک قابل اعتماد به ثبت درست جزئیات در سندها وابسته است.',
        'کم بودن یا نبود داده را به معنی سودآور نبودن یک پروژه یا مشتری نگیر.',
      ],
    },
  },
];
